import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { useBilling } from "../context/BillingContext";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, Crown, Building } from "lucide-react";
import { CancelFeedbackModal } from "../components/CancelFeedbackModal";

const ICONS = { entry: Sparkles, executive: Crown, enterprise: Building };

export default function Billing() {
  const { info, refresh } = useBilling();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => { (async () => {
    const { data } = await api.get("/billing/plans"); setPlans(data.plans);
  })(); }, []);

  const upgrade = async (plan, feedback = null) => {
    if (plan === "entry" && !feedback && (info?.plan || "entry") !== "entry") { setLeaving(true); return; }
    setBusy(plan);
    try {
      if (plan === "entry") {
        await api.post("/billing/upgrade", { plan, ...(feedback || {}) });
        await refresh();
        setLeaving(false);
        toast.success("Switched to ENTRY");
      } else {
        const { data } = await api.post("/billing/checkout", { plan, billing_cycle: cycle, origin_url: window.location.origin });
        window.location.href = data.url;
        return;
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || (typeof d === "string" ? d : "Couldn't change plan"));
    }
    setBusy(null);
  };

  const manageBilling = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
      return;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't open billing"); }
    setBusy(null);
  };

  const current = info?.plan || "entry";
  const usage = info?.usage;

  return (
    <AppShell eyebrow="Plans" title="Pricing & Subscription">
      <CancelFeedbackModal open={leaving} onClose={() => setLeaving(false)} onConfirm={(fb) => upgrade("entry", fb)} />
      <p className="text-sm text-zinc-600 max-w-2xl mb-8">
        Start free. Upgrade when your rhythm picks up. Lead together when you're ready.
      </p>

      {usage && (
        <div className="border border-zinc-200 p-5 mb-8 brand-gradient-soft" data-testid="usage-card">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#0033FF] font-bold mb-2">Current period</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            <Usage label="AI prompts" used={usage.ai_prompts_used} limit={usage.ai_prompts_limit} testid="usage-ai" />
            <Usage label="Team entries" used={usage.team_count} limit={usage.team_limit} testid="usage-team" />
            <Usage label="Client entries" used={usage.customers_count} limit={usage.customers_limit} testid="usage-customers" />
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-6 justify-center sm:justify-start">
        <span className="text-xs tracking-wider uppercase text-zinc-500 font-bold mr-2">Billing</span>
        <button onClick={()=>setCycle("monthly")} data-testid="cycle-monthly"
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border ${cycle==="monthly" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}>Monthly</button>
        <button onClick={()=>setCycle("yearly")} data-testid="cycle-yearly"
          className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border ${cycle==="yearly" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}>Yearly · save 29%</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        {plans.length === 0 && [0, 1, 2].map((i) => (
          <div key={i} className="bg-white p-8 h-96 animate-pulse" data-testid={`plan-skeleton-${i}`}>
            <div className="h-6 w-1/2 bg-zinc-100 mb-4" /><div className="h-10 w-1/3 bg-zinc-100 mb-6" />
            <div className="space-y-2">{[0, 1, 2, 3].map((j) => <div key={j} className="h-4 bg-zinc-100" />)}</div>
          </div>
        ))}
        {plans.map((p) => {
          const Icon = ICONS[p.key] || Sparkles;
          const isCurrent = p.key === current;
          const isMostPopular = p.key === "executive";
          const price = p.key === "enterprise" ? `$${p.price_monthly_base}` : (cycle === "yearly" && p.price_yearly !== undefined ? `$${p.price_yearly}` : `$${p.price_monthly}`);
          const priceSuffix = p.key === "enterprise" ? `/mo · up to ${p.base_seats} seats` : (cycle === "yearly" ? "/yr" : "/mo");
          return (
            <div key={p.key} className={`bg-white p-6 sm:p-8 flex flex-col ${isMostPopular ? "border-t-4 border-[#0033FF] -mt-1" : ""}`} data-testid={`plan-card-${p.key}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className={`w-10 h-10 ${isMostPopular ? "bg-[#0033FF]" : "bg-zinc-900"} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${isMostPopular ? "text-white" : "text-[#FFB800]"}`} />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500">Tier</div>
                    <div className="text-xl font-black tracking-tighter">{p.name}</div>
                  </div>
                </div>
                {isMostPopular && <div className="text-[9px] tracking-[0.25em] uppercase font-bold bg-[#FFB800] text-zinc-900 px-2 py-1">Most popular</div>}
              </div>
              <div className="text-sm text-zinc-600 mb-4">{p.tagline}</div>
              <div className="mb-6">
                <div className="text-4xl font-black tracking-tighter">{price}<span className="text-base text-zinc-500 font-medium">{priceSuffix}</span></div>
                {p.key === "enterprise" && <div className="text-xs text-zinc-500 mt-1">+ ${p.price_per_extra_seat}/seat up to {p.max_seats}</div>}
                {p.key === "executive" && cycle === "yearly" && <div className="text-xs text-[#0033FF] font-bold mt-1">Save 29% vs monthly</div>}
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {p.highlights.map((h, i) => (
                  <li key={i} className="text-sm flex gap-2"><Check className="w-4 h-4 text-[#0033FF] shrink-0 mt-0.5" /><span>{h}</span></li>
                ))}
              </ul>
              {isCurrent ? (
                <div className="border border-zinc-900 px-4 py-3 text-xs font-bold uppercase tracking-wider text-center" data-testid={`current-plan-${p.key}`}>Current plan</div>
              ) : (
                <button onClick={()=>upgrade(p.key)} disabled={busy === p.key} data-testid={`upgrade-to-${p.key}`}
                  className={`px-4 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 ${isMostPopular ? "bg-[#0033FF] text-white hover:bg-zinc-900" : "border border-zinc-900 hover:bg-zinc-900 hover:text-white"} disabled:opacity-50`}>
                  {busy === p.key ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/>{p.key === "entry" ? "Switching…" : "Opening checkout…"}</> : (current === "enterprise" || (current === "executive" && p.key === "entry") ? "Switch" : "Choose plan")}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        {info?.has_subscription && (
          <button onClick={manageBilling} disabled={busy === "portal"} data-testid="manage-billing-btn"
            className="border border-zinc-900 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white disabled:opacity-50 inline-flex items-center gap-2">
            {busy === "portal" && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Manage billing & invoices
          </button>
        )}
        <div className="text-xs text-zinc-500" data-testid="billing-note">
          Paid plans renew automatically and are billed securely by Stripe. Cancel anytime by switching to Entry.
        </div>
      </div>
    </AppShell>
  );
}

function Usage({ label, used, limit, testid }) {
  const unlimited = limit == null;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const tone = unlimited ? "text-zinc-500" : pct >= 100 ? "text-[#FF3B30]" : pct >= 80 ? "text-[#FFB800]" : "text-[#0033FF]";
  return (
    <div className="bg-white p-4" data-testid={testid}>
      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500">{label}</div>
      <div className={`text-2xl font-black tracking-tighter mt-1 ${tone}`}>
        {used}<span className="text-base text-zinc-500"> / {unlimited ? "∞" : limit}</span>
      </div>
      {!unlimited && (
        <div className="h-1 bg-zinc-100 mt-2">
          <div className={`h-full ${pct >= 100 ? "bg-[#FF3B30]" : pct >= 80 ? "bg-[#FFB800]" : "bg-[#0033FF]"}`} style={{width: `${pct}%`}} />
        </div>
      )}
    </div>
  );
}
